import { Visibility } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import {
  ClusterShipperThirdPartyConversionDeliveryDto,
  NeedsClusterReinstalledClusterWithOdometerPhotoDto,
  NeedsClusterReinstalledComboPhotoDto,
  NeedsClusterReinstalledImporterDoorLabelPhotoDto,
  VehicleOdometerAfterModificationPhotoDto,
  NeedsClusterRemovedPulledClusterPhotoDto,
  VehicleCheckInCheckInClusterPhotoDto,
  VehicleDto
} from 'dtos'
import VehiclePhotoCard from './VehiclePhotoCard'
import { useDialogState } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import ClusterPhotosViewDialog from './ClusterPhotosViewDialog'
import { enqueueSnackbar } from 'notistack'

interface ClusterPhotosListCardProps {
  vehicle: VehicleDto
}

export default function ClusterPhotosListCard({ vehicle }: ClusterPhotosListCardProps) {
  const [dialogState, setDialogState] = useDialogState({
    clusterPhotosViewDialogOpen: false
  })

  const [selectedPhotoTypes, setSelectedPhotoTypes] = useState<string | null>(null)

  const togglePhotoTypeSelection = (type: string) => {
    setSelectedPhotoTypes(prevSelected => (prevSelected === type ? null : type))
  }

  const [photos, setPhotos] = useState<{
    checkInClusterPhoto?: VehicleCheckInCheckInClusterPhotoDto
    pulledClusterPhoto?: NeedsClusterRemovedPulledClusterPhotoDto
    thirdPartyConversionDeliveryPhoto?: ClusterShipperThirdPartyConversionDeliveryDto
    clusterWithOdometerPhoto?: NeedsClusterReinstalledClusterWithOdometerPhotoDto
    importerDoorLabelPhoto?: NeedsClusterReinstalledImporterDoorLabelPhotoDto
    comboPhoto?: NeedsClusterReinstalledComboPhotoDto
    odometerAfterModificationPhoto?: VehicleOdometerAfterModificationPhotoDto
  }>({})

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      controllers.getVehicleCheckInCheckInClusterPhotoInformation(vehicle.vehicleGuid).then(response => {
        setPhotos(prev => ({
          ...prev,
          checkInClusterPhoto: response.value
        }))
      })

      controllers.getNeedsClusterRemovedPulledClusterPhotoInformation(vehicle.vehicleGuid).then(response => {
        setPhotos(prev => ({
          ...prev,
          pulledClusterPhoto: response.value
        }))
      })

      if (vehicle.clusterShipper?.clusterShipperGuid) {
        controllers
          .getClusterShippersThirdPartyConversionDeliveryInformation(vehicle.clusterShipper.clusterShipperGuid)
          .then(response => {
            setPhotos(prev => ({
              ...prev,
              thirdPartyConversionDeliveryPhoto: response.value
            }))
          })
      }

      controllers.getNeedsClusterReinstalledClusterWithOdometerLabelPhotoInformation(vehicle.vehicleGuid).then(response => {
        setPhotos(prev => ({
          ...prev,
          clusterWithOdometerPhoto: response.value
        }))
      })

      controllers.getNeedsClusterReinstalledImporterDoorLabelPhotoInformation(vehicle.vehicleGuid).then(response => {
        setPhotos(prev => ({
          ...prev,
          importerDoorLabelPhoto: response.value
        }))
      })

      controllers.getNeedsClusterReinstalledComboLabelPhotoInformation(vehicle.vehicleGuid).then(response => {
        setPhotos(prev => ({
          ...prev,
          comboPhoto: response.value
        }))
      })

      controllers.getVehicleOdometerAfterModificationPhoto(vehicle.vehicleGuid).then(response => {
        setPhotos(prev => ({
          ...prev,
          odometerAfterModificationPhoto: response.value
        }))
      })
    }
  }, [vehicle])

  const availablePhotoTypes: Array<
    | 'check-in-cluster'
    | 'pulled-cluster'
    | 'third-party-conversion-delivery'
    | 'cluster-with-odometer'
    | 'importer-door-label'
    | 'combo'
    | 'odometer-after-modification'
  > = useMemo(() => {
    const types: Array<
      | 'check-in-cluster'
      | 'pulled-cluster'
      | 'third-party-conversion-delivery'
      | 'cluster-with-odometer'
      | 'importer-door-label'
      | 'combo'
      | 'odometer-after-modification'
    > = []

    if (photos.checkInClusterPhoto?.documentGuid) {
      types.push('check-in-cluster')
    }

    if (photos.pulledClusterPhoto?.documentGuid) {
      types.push('pulled-cluster')
    }

    if (photos.thirdPartyConversionDeliveryPhoto?.clusterShipperThirdPartyConversionDeliveryPhotoFileName) {
      types.push('third-party-conversion-delivery')
    }

    if (photos.clusterWithOdometerPhoto?.documentGuid) {
      types.push('cluster-with-odometer')
    }

    if (photos.importerDoorLabelPhoto?.documentGuid) {
      types.push('importer-door-label')
    }

    if (photos.comboPhoto?.documentGuid) {
      types.push('combo')
    }
    if (photos.odometerAfterModificationPhoto?.documentGuid) {
      types.push('odometer-after-modification')
    }
    return types
  }, [
    photos.checkInClusterPhoto?.documentGuid,
    photos.pulledClusterPhoto?.documentGuid,
    photos.thirdPartyConversionDeliveryPhoto?.clusterShipperThirdPartyConversionDeliveryPhotoFileName,
    photos.clusterWithOdometerPhoto?.documentGuid,
    photos.importerDoorLabelPhoto?.documentGuid,
    photos.comboPhoto?.documentGuid,
    photos.odometerAfterModificationPhoto?.documentGuid
  ])

  return (
    <>
      <ClusterPhotosViewDialog
        key={selectedPhotoTypes}
        photoType={
          selectedPhotoTypes as
            | 'check-in-cluster'
            | 'pulled-cluster'
            | 'third-party-conversion-delivery'
            | 'cluster-with-odometer'
            | 'importer-door-label'
            | 'combo'
            | 'odometer-after-modification'
        }
        photoTypes={availablePhotoTypes}
        vehicleGuid={vehicle.vehicleGuid!}
        clusterShipperGuid={vehicle.clusterShipper?.clusterShipperGuid}
        onClose={() => {
          setDialogState('clusterPhotosViewDialogOpen', false)
          setSelectedPhotoTypes(null)
        }}
        open={dialogState.clusterPhotosViewDialogOpen}
      />
      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Cluster Photos
                </Typography>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Button
                  onClick={() => {
                    if (!selectedPhotoTypes) {
                      enqueueSnackbar('Please select a photo to view.', { variant: 'warning' })
                    } else {
                      setDialogState('clusterPhotosViewDialogOpen', true)
                    }
                  }}
                  variant='text'
                  endIcon={<Visibility />}
                >
                  VIEW SELECTED
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item container spacing={2} alignItems='center' justifyContent='center' sx={{ mt: 1, mb: 3 }}>
              <Grid item xs={6} sm={6}>
                <VehiclePhotoCard source='cluster-photos'
                  complete={Boolean(photos.checkInClusterPhoto?.documentGuid)}
                  documentGuid={photos.checkInClusterPhoto?.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('check-in-cluster')
                  }}
                  type='check-in-cluster'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'check-in-cluster'}
                  label='Speedometer Before Modification'
                />
              </Grid>

              <Grid item xs={6} sm={6}>
                <VehiclePhotoCard source='cluster-photos'
                  complete={Boolean(photos.pulledClusterPhoto?.documentGuid)}
                  documentGuid={photos.pulledClusterPhoto?.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('pulled-cluster')
                  }}
                  type='pulled-cluster'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'pulled-cluster'}
                  label='Pulled Cluster'
                />
              </Grid>

              {/* 3rd Party Conversion Delivery remains ClusterShipper-owned (not VehicleDocuments); displayed via the vehicle's ClusterShipper. */}
              <Grid item xs={6} sm={6}>
                <VehiclePhotoCard source='cluster-photos'
                  complete={Boolean(
                    photos.thirdPartyConversionDeliveryPhoto?.clusterShipperThirdPartyConversionDeliveryPhotoFileName
                  )}
                  fileName={photos.thirdPartyConversionDeliveryPhoto?.clusterShipperThirdPartyConversionDeliveryPhotoFileName}
                  onClick={() => {
                    togglePhotoTypeSelection('third-party-conversion-delivery')
                  }}
                  type='third-party-conversion-delivery'
                  vehicleGuid={vehicle.vehicleGuid}
                  clusterShipperGuid={vehicle.clusterShipper?.clusterShipperGuid}
                  selected={selectedPhotoTypes === 'third-party-conversion-delivery'}
                  label='3rd Party Conversion Delivery'
                />
              </Grid>

              <Grid item xs={6} sm={6}>
                <VehiclePhotoCard source='cluster-photos'
                  complete={Boolean(photos.clusterWithOdometerPhoto?.documentGuid)}
                  documentGuid={photos.clusterWithOdometerPhoto?.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('cluster-with-odometer')
                  }}
                  type='cluster-with-odometer'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'cluster-with-odometer'}
                  label='Speedometer After Modification'
                />
              </Grid>

              <Grid item xs={6} sm={6}>
                <VehiclePhotoCard source='cluster-photos'
                  complete={Boolean(photos.odometerAfterModificationPhoto?.documentGuid)}
                  documentGuid={photos.odometerAfterModificationPhoto?.documentGuid}
                  label='Odometer After Modification'
                  onClick={() => {
                    togglePhotoTypeSelection('odometer-after-modification')
                  }}
                  selected={selectedPhotoTypes === 'odometer-after-modification'}
                  type='odometer-after-modification'
                  vehicleGuid={vehicle.vehicleGuid}
                />
              </Grid>

              <Grid item xs={6} sm={6}>
                <VehiclePhotoCard source='cluster-photos'
                  complete={Boolean(photos.importerDoorLabelPhoto?.documentGuid)}
                  documentGuid={photos.importerDoorLabelPhoto?.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('importer-door-label')
                  }}
                  type='importer-door-label'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'importer-door-label'}
                  label='Importer Door Label'
                />
              </Grid>

              <Grid item xs={6} sm={6}>
                <VehiclePhotoCard source='cluster-photos'
                  complete={Boolean(photos.comboPhoto?.documentGuid)}
                  documentGuid={photos.comboPhoto?.documentGuid}
                  onClick={() => {
                    togglePhotoTypeSelection('combo')
                  }}
                  type='combo'
                  vehicleGuid={vehicle.vehicleGuid}
                  selected={selectedPhotoTypes === 'combo'}
                  label='Combo'
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**VIEW SELECTED Button:**

The “VIEW SELECTED” button is only available if the user has selected photos to view.

Clicking the “VIEW SELECTED” button will open the View Check In Photo Dialog.

**Speedometer Before Modification:**

Displays the Speedometer Before Modification image uploaded during the Vehicle Check-In process.

If no image has been uploaded, the “No Image” icon will be displayed instead.

Clicking the Speedometer Before Modification image will select it.

**Pulled Cluster:**

Displays the Pulled Cluster image uploaded from the Needs Cluster Removed To-Do List.

If no image has been uploaded, the “No Image” icon will be displayed instead.

Clicking the Pulled Cluster image will select it.

**3rd Party Conversion Delivery:**

Displays the 3rd Party Conversion Delivery image uploaded from the Cluster Shipper Details.

If no image has been uploaded, the “No Image” icon will be displayed instead.

Clicking the 3rd Party Conversion Delivery image will select it.

**Speedometer After Modification:**

Displays the Speedometer After Modification image uploaded from the Needs Cluster Reinstalled To-Do List.

If no image has been uploaded, the “No Image” icon will be displayed instead.

Clicking the Speedometer After Modification image will select it.

**Importer Door Label:**

Displays the Importer Door Label image uploaded from the Needs Cluster Reinstalled To Do List.

If no image has been uploaded the “No Image” icon will be displayed instead.

Clicking the Importer Door Label will select the image.

**Combo:**

Displays the Combo image uploaded from the Needs Cluster Reinstalled To-Do List.

If no image has been uploaded, the “No Image” icon will be displayed instead.

Clicking the Combo image will select it.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
